/**
 * @file DotPlotContainer component
 * @description Container component that handles business logic for the DotPlot visualization.
 * Manages calculations for team average, scale generation, position calculations, and
 * metric-specific logic such as capping player_style_fit values at 100.
 */

import { useCallback, useMemo } from "react";

import DotPlotView from "./DotPlotView";

import useMobileScreen from "@/hooks/useMobileScreen";
import createMetricScale from "@/utils/metricScale";

/**
 * Props for DotPlot component
 */
interface DotPlotContainerProps {
  /** Current player's metric value */
  currentValue: number;
  /** Array of roster players' metric values for comparison */
  rosterValues: number[];
  /** Player's last name to display on the chart */
  lastName: string;
  /** Whether dark mode styling should be applied */
  isDark: boolean;
  /** Optional metric identifier for special handling (e.g., player_style_fit) */
  metricId?: string;
}

/**
 * DotPlot container component
 * @param root0 - The component props
 * @param root0.currentValue - Current player's metric value
 * @param root0.rosterValues - Array of roster players' metric values for comparison
 * @param root0.lastName - Player's last name to display on the chart
 * @param root0.isDark - Whether dark mode styling should be applied
 * @param root0.metricId - Optional metric identifier for special handling (e.g., player_style_fit)
 * @remarks
 * Separates business logic from presentation. Calculates team averages, scale values,
 * and position percentages for displaying a dot plot comparison chart.
 */
function DotPlotContainer({
  currentValue,
  rosterValues,
  lastName,
  isDark,
  metricId,
}: DotPlotContainerProps) {
  const isSmallScreen = useMobileScreen();

  const teamAverage = useMemo(() => {
    return rosterValues.reduce((a, b) => a + b, 0) / rosterValues.length;
  }, [rosterValues]);

  const highestValue = useMemo(() => {
    const allValues = [...rosterValues, currentValue];
    if (allValues.length === 0) return 0;

    const maxValue = Math.max(...allValues);
    // Cap player_style_fit at 100 instead of 200
    return metricId === "player_style_fit" ? Math.min(maxValue, 100) : maxValue;
  }, [rosterValues, currentValue, metricId]);

  const { maxScale, dotPlotLabelArray } = useMemo(() => {
    const scales = createMetricScale(highestValue || 0);
    const calculatedMaxScale = scales?.at(-1) || 0;

    // Ensure player_style_fit scale never exceeds 100
    const finalMaxScale =
      metricId === "player_style_fit"
        ? Math.min(calculatedMaxScale, 100)
        : calculatedMaxScale;

    // Regenerate scale labels if we capped the max scale for player_style_fit
    const finalScales =
      metricId === "player_style_fit" && calculatedMaxScale > 100
        ? [0, 25, 50, 75, 100]
        : scales || [];

    return {
      maxScale: finalMaxScale,
      dotPlotLabelArray: finalScales,
    };
  }, [highestValue, metricId]);

  const getPosition = useCallback(
    (value: number) => {
      return (value / maxScale) * 100;
    },
    [maxScale]
  );

  return (
    <DotPlotView
      currentValue={currentValue}
      rosterValues={rosterValues}
      lastName={lastName}
      isDark={isDark}
      isSmallScreen={isSmallScreen}
      teamAverage={teamAverage}
      highestValue={highestValue}
      maxScale={maxScale}
      dotPlotLabelArray={dotPlotLabelArray}
      getPosition={getPosition}
    />
  );
}

export default DotPlotContainer;
